import { makeObservable, observable, action, computed, runInAction } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { uuidModule } from "@/modules/uuid";
import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import { resolveCapturedAudioSessionSyncModelUuid } from "@/modules/uuid/sync-models/resolveCapturedAudioSessionSyncModelUuid";
import { Uuid } from "@/domains/global/identifiers";
import { NoteObservable } from "@/store/note/NoteObservable";
import { clientEnvModule } from "@/modules/client-env";
import { TranscriptionManager } from "@/store/audio-capture/transcription/managers/web-transcription-manager";
import type { InterleavedTranscriptItem } from "@/store/audio-capture/transcription/utils/transcript-interleaver";
import { UploadManager } from "@/store/audio-capture/processing/upload/upload-manager";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
export { RecordingStatus };
import { Duration } from "luxon";
import type { AudioLevelProbe } from "@/store/audio-capture/interfaces/audio-level-probe";
import { VoiceAnalyticsTracker } from "@/store/audio-capture/analytics/voice-analytics-tracker";
import {
  DesktopRecorderEngine,
  WebRecorderEngine,
  RecorderEngine,
} from "@/store/audio-capture/engines";
import { TranscriptionCoordinator } from "@/store/audio-capture/transcription/services/transcription-coordinator";
import {
  formatDurationSeconds,
  computeIsSpeakDisabled,
  computeIsIndicatorVisible,
  computeIsNoteRecording,
  computeHasFinishedAudioRecording,
} from "@/store/audio-capture/ui/selectors/ui-state";
import {
  RecallPermissionKind,
  RecallPermissionMap,
  RecallPermissionStatus,
} from "@/modules/electron/types";
import type {
  AudioCapturePersistence,
  PersistedAudioCaptureState,
  RecordingEngineType,
  RecordingSessionUpdateArgs,
} from "@/store/audio-capture/persistence/audio-capture-persistence";
import { CoordinatorAudioCapturePersistence } from "@/store/audio-capture/persistence/coordinator-audio-capture-persistence";
import { AudioRecordingCoordinator } from "@/store/audio-capture/coordinator";
import { withAudioCaptureLock, triggerAutoStop } from "@/store/audio-capture/utils/session";
import { formatDurationLimitLabel } from "@/store/audio-capture/utils/duration-formatting";
import { buildRecordingInProgressToastContent } from "@/store/audio-capture/utils/user-feedback";
import type { TranscriptionChannelManager } from "@/store/audio-capture/transcription/interfaces";
import {
  SessionManager,
  DeviceManager,
  ProcessingManager,
  UiTelemetryManager,
  LifecycleManager,
  WorkflowManager,
  TranscriptionFacade,
  ObservabilityManager,
} from "@/store/audio-capture/managers";
import type {
  RecordingUploadPayload,
  TransformAudioArgs,
} from "@/store/audio-capture/managers/processing-manager";

export type AutoStopReason = "duration-limit";

export interface AudioCaptureStore {}

type RecordingSessionContext = {
  noteId: string;
  recordingId: string;
  capturedAudioId: string;
  engine: RecordingEngineType;
  startedAt: number;
  isRecordingWithoutTranscription: boolean;
};

export class AppStoreAudioCaptureStore extends AppSubStore implements AudioCaptureStore {
  readonly SAMPLE_RATE = 16000; // AssemblyAI expects 16kHz
  readonly DURATION_UPDATE_INTERVAL_MS = 1000; // 1 second
  readonly SILENCE_DURATION_THRESHOLD_MS = 60_000; // 1 minute
  readonly POST_STOP_BUFFER_MS = 1000; // 1 second
  readonly START_CANCELLED_ERROR_MESSAGE = "Start cancelled";
  readonly MAX_RECORDING_DURATION = Duration.fromObject({ hours: 2 });
  readonly DESKTOP_INITIALIZATION_BUFFER = Duration.fromObject({ milliseconds: 700 });

  // System Audio Settings

  // Permissions
  showPermissionModal: boolean = false;
  hasSystemAudioRecordingPermission: boolean = false;
  hasMicrophoneRecordingPermission: boolean = false;
  hasAccessibilityPermission: boolean = false;
  recallPermissionStatus: RecallPermissionMap = {};

  // Global state for audio capture
  activeNoteId: string | null = null;
  capturedAudioId: string = uuidModule.generate();
  currentSession: RecordingSessionContext | null = null;

  status: RecordingStatus = RecordingStatus.IDLE;
  error: string | null = null;

  // Desktop initialization buffer
  desktopInitializationBufferUntil: number | null = null;
  desktopInitializationBufferTimer: ReturnType<typeof setTimeout> | null = null;

  // Audio restoration
  restoredDuration: number = 0;

  // Audio recording
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  audioBlob: Blob | null = null;
  audioUrl: string | null = null;
  uploadProgressPercent: number | null = null;
  uploadManager: UploadManager | null = null;
  releaseRecordingLock: ((value: unknown) => void) | null = null;
  activeAudioInputDevices: { deviceId: string; groupId: string }[] = [];
  activeAudioInputDeviceLabel: string | null = null;
  isMicrophoneDisconnected: boolean = false;
  defaultAudioInputDeviceId: string | undefined = undefined;
  audioIsSilentSince: number | null = null;
  // Audio streaming + transcription
  audioContext: AudioContext | null = null;
  transcriptions: TranscriptionChannelManager[] = [];
  isRecordingWithoutTranscription: boolean = false;

  // Audio Visualization
  analyser: AnalyserNode | null = null;
  visualizer: AudioLevelProbe | null = null;
  private readonly analytics = new VoiceAnalyticsTracker();
  private readonly desktopRecorder: DesktopRecorderEngine;
  private readonly webRecorder: WebRecorderEngine;
  readonly transcriptionCoordinator: TranscriptionCoordinator;
  readonly transcription: TranscriptionFacade;
  private _persistence: AudioCapturePersistence | null = null;
  private recordingCoordinator: AudioRecordingCoordinator | null = null;
  private readonly workflow: WorkflowManager;
  readonly session: SessionManager;
  readonly device: DeviceManager;
  readonly processing: ProcessingManager;
  readonly ui: UiTelemetryManager;
  readonly lifecycle: LifecycleManager;
  readonly observability: ObservabilityManager;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.session = new SessionManager(this);
    this.device = new DeviceManager(this);
    this.processing = new ProcessingManager(this);
    this.ui = new UiTelemetryManager(this);
    this.observability = new ObservabilityManager(this);
    this.transcriptions = [new TranscriptionManager(this, 0)];
    this.transcriptionCoordinator = new TranscriptionCoordinator(() => this.transcriptions);
    this.transcription = new TranscriptionFacade(this.transcriptionCoordinator);

    makeObservable<
      this,
      | "desktopInitializationBufferTimer"
      | "updateFriendlyDebugInfo"
      | "_triggerAutoStop"
      | "analytics"
      | "desktopRecorder"
      | "webRecorder"
      | "getRecorderEngine"
      | "observability"
      | "desktopInitializationBufferUntil"
      | "completeCurrentSession"
      | "updateSession"
      | "setAudioSilenceSince"
      | "updateUploadProgress"
      | "beginRecordingSession"
      | "restoreRecordingSessionFromState"
      | "_persistence"
      | "recordingCoordinator"
      | "workflow"
    >(this, {
      workflow: true,
      transcription: true,
      session: true,
      device: true,
      processing: true,
      ui: true,
      lifecycle: true,
      recordingCoordinator: false,
      persistence: computed,
      _persistence: false,
      currentSession: observable,
      beginRecordingSession: action,
      restoreRecordingSessionFromState: action,
      completeCurrentSession: action,
      updateSession: action,
      setAudioSilenceSince: action,
      updateUploadProgress: action,
      transcriptionCoordinator: true,
      analytics: true,
      desktopRecorder: true,
      webRecorder: true,
      getRecorderEngine: true,
      observability: true,
      setStatus: true,
      setPausedTime: true,
      incrementTotalPausedTime: true,
      clearDurationInterval: true,
      startDurationInterval: true,
      resetAutoStopState: true,
      _detectAudioInputDevices: true,
      DESKTOP_INITIALIZATION_BUFFER: true,
      desktopInitializationBufferTimer: true,
      hasRecordingForNote: true,
      updateFriendlyDebugInfo: true,
      _triggerAutoStop: true,
      autoStopDialogContent: computed,

      // Permissions
      showPermissionModal: observable,
      setShowPermissionModal: action,
      syncRecallPermissions: action,
      setRecallPermissionStatus: action,
      screenCapturePermissionStatus: computed,
      microphonePermissionStatus: computed,
      accessibilityPermissionStatus: computed,
      recallPermissionStatus: observable,
      hasSystemAudioRecordingPermission: observable,
      hasMicrophoneRecordingPermission: observable,
      hasAccessibilityPermission: observable,
      autoStopModalVisible: computed,
      autoStopReason: computed,
      autoStopDurationSeconds: computed,
      setAutoStopModalVisible: action,

      // Constants
      DURATION_UPDATE_INTERVAL_MS: false,
      SAMPLE_RATE: false,
      SILENCE_DURATION_THRESHOLD_MS: true,
      POST_STOP_BUFFER_MS: false,
      START_CANCELLED_ERROR_MESSAGE: false,
      MAX_RECORDING_DURATION: false,

      // Global Audio Capture State
      activeNoteId: observable,
      activeNoteObservable: computed,
      capturedAudioId: observable,
      sessionId: computed,
      maxRecordingDuration: computed,
      maxRecordingDurationLimitLabel: computed,
      supportedMimeType: computed,

      status: observable,
      isNoteRecording: computed,
      hasFinishedAudioRecording: computed,
      isSpeakDisabled: computed,
      isIndicatorVisible: computed,
      isRecordingWithoutTranscription: observable,
      error: observable,
      uploadProgressPercent: observable,
      uploadManager: observable,

      // Audio metadata
      durationInterval: computed,
      duration: computed,
      startTime: computed,
      pausedTime: computed,
      totalPausedTime: computed,
      desktopInitializationBufferUntil: observable,
      formattedDuration: computed,
      recordingStatus: computed,
      recordingProgress: computed,
      currentTranscript: computed,

      // Audio restoration
      restoredDuration: observable,

      // Audio Recording
      mediaRecorder: observable,
      audioChunks: observable,
      audioBlob: observable,
      audioUrl: observable,
      releaseRecordingLock: observable,
      friendlyDebugInfo: computed,
      activeAudioInputDevices: observable,
      activeAudioInputDeviceLabel: observable,
      isMicrophoneDisconnected: observable,
      defaultAudioInputDeviceId: observable,
      audioIsSilentSince: observable,
      isDesktop: computed,
      isDesktopInitializationBufferActive: computed,
      startDesktopInitializationBuffer: action,
      clearDesktopInitializationBuffer: action,
      // Audio Visualization / Analysis
      analyser: observable,

      // HELPERS (Recording)
      _uploadRecording: action,
      _transformAudioToNote: action,
      _withAudioCaptureLock: action,
      _updateDuration: action,
      handleMediaRecorderData: false,

      // Audio Streaming + Transcription
      audioContext: observable,
      visualizer: observable,
      transcript: computed,
      transcriptArray: computed,
      fullTranscript: computed,
      interleavedTranscriptInfo: computed,
      transcriptions: observable,
      heartbeatForComputedValues: computed,
      isTranscriptionPaused: computed,
      isAudioSilent: computed,

      // External Actions
      initializeFromDb: action,
      startRecording: action,
      pauseRecording: action,
      resumeRecording: action,
      stopRecording: action,
      processRecording: action,
      resetRecording: action,
      clearRecording: action,
      clearAudioCapturesByNoteId: action,

      // Utilities
    });

    this.desktopRecorder = new DesktopRecorderEngine(this, this.analytics);
    this.webRecorder = new WebRecorderEngine(this, this.analytics);
    this.lifecycle = new LifecycleManager(this, this.desktopRecorder, this.webRecorder);
    this.workflow = new WorkflowManager(this, this.analytics, this.processing);

    this.updateFriendlyDebugInfo();

    const isDesktop = clientEnvModule.isDesktop();
    window.addEventListener("beforeunload", event => {
      if (this.status === RecordingStatus.RECORDING) {
        event.preventDefault();
        event.stopPropagation();
        const note = this.activeNoteObservable;
        if (isDesktop && note) {
          toastModule.triggerToast({
            content: buildRecordingInProgressToastContent(note.title, () =>
              this.store.navigation.goToNote({ noteId: note.id })
            ),
            toastId: this.capturedAudioId,
          });
        }
        return "Audio is being captured. Are you sure you want to leave?";
      }
    });
  }

  get activeNoteObservable(): NoteObservable | undefined {
    if (!this.activeNoteId) return undefined;
    return this.store.notes.get(this.activeNoteId);
  }

  get sessionId(): Uuid {
    return resolveCapturedAudioSessionSyncModelUuid({
      capturedAudioId: this.capturedAudioId,
    });
  }

  get maxRecordingDuration(): Duration {
    return this.MAX_RECORDING_DURATION;
  }

  get persistence(): AudioCapturePersistence {
    if (!this._persistence) {
      if (!this.recordingCoordinator) {
        this.recordingCoordinator = new AudioRecordingCoordinator(this.store.memDb.audioRecordings);
      }

      this._persistence = new CoordinatorAudioCapturePersistence(
        this.recordingCoordinator,
        this.store.memDb.audioRecordings,
        this.store.memDb.audioRecordingMetadata,
        this.store.memDb.audioRecordingCapturedAudio
      );
    }
    return this._persistence;
  }

  get maxRecordingDurationLimitLabel(): string {
    return formatDurationLimitLabel(this.MAX_RECORDING_DURATION);
  }

  get formattedDuration(): string {
    return formatDurationSeconds(this.duration, this.restoredDuration);
  }

  get duration(): number {
    return this.session.duration;
  }

  get startTime(): number {
    return this.session.startTime;
  }

  get pausedTime(): number {
    return this.session.pausedTime;
  }

  get totalPausedTime(): number {
    return this.session.totalPausedTime;
  }

  get durationInterval(): NodeJS.Timeout | null {
    return this.session.durationInterval;
  }

  get autoStopModalVisible(): boolean {
    return this.session.autoStopModalVisible;
  }

  get autoStopReason(): AutoStopReason | null {
    return this.session.autoStopReason;
  }

  get autoStopDurationSeconds(): number | null {
    return this.session.autoStopDurationSeconds;
  }

  get recordingStatus(): RecordingStatus {
    return this.status;
  }

  get recordingProgress(): {
    elapsedSeconds: number;
    restoredSeconds: number;
    formatted: string;
    autoStopReason: AutoStopReason | null;
  } {
    return {
      elapsedSeconds: this.duration,
      restoredSeconds: this.restoredDuration,
      formatted: this.formattedDuration,
      autoStopReason: this.autoStopReason,
    };
  }

  get currentTranscript(): string {
    return this.transcript;
  }

  get friendlyDebugInfo(): Record<string, unknown> {
    return this.ui.friendlyDebugInfo;
  }

  get heartbeatForComputedValues(): number {
    return this.ui.heartbeatForComputedValues;
  }

  get supportedMimeType(): string | undefined {
    const preferred = "audio/webm";
    const fallback = "audio/mp4";
    const mime = MediaRecorder.isTypeSupported(preferred)
      ? preferred
      : MediaRecorder.isTypeSupported(fallback)
        ? fallback
        : undefined;
    return mime;
  }

  get isNoteRecording(): boolean {
    return computeIsNoteRecording(this.status, this.activeNoteId);
  }

  get hasFinishedAudioRecording() {
    return computeHasFinishedAudioRecording(this.status);
  }

  get isSpeakDisabled() {
    return computeIsSpeakDisabled(
      this.status,
      this.releaseRecordingLock,
      this.activeNoteObservable?.isDeleted
    );
  }

  get isIndicatorVisible() {
    return computeIsIndicatorVisible(this.isSpeakDisabled, this.status);
  }

  get isDesktopInitializationBufferActive(): boolean {
    return (
      this.desktopInitializationBufferUntil !== null &&
      Date.now() < this.desktopInitializationBufferUntil
    );
  }

  get isAudioSilent() {
    if (!this.audioIsSilentSince) return false;
    const silenceDuration = this.heartbeatForComputedValues - this.audioIsSilentSince;
    return silenceDuration > this.SILENCE_DURATION_THRESHOLD_MS;
  }

  get transcript() {
    return this.transcription.transcriptString(this.transcriptions);
  }

  get transcriptArray() {
    return this.transcription.transcriptArray(this.transcriptions);
  }

  get fullTranscript(): InterleavedTranscriptItem[] {
    // Already computed in util; items include isLastOfBlock
    return this.interleavedTranscriptInfo;
  }

  get interleavedTranscriptInfo(): InterleavedTranscriptItem[] {
    return this.transcription.interleavedTranscript(this.transcriptions);
  }

  get isTranscriptionPaused(): boolean {
    // Only report pause state while a session is actively recording; paused sessions show the standard UI affordances.
    const isRecording = this.status === RecordingStatus.RECORDING;
    if (!isRecording) {
      return false;
    }

    // Transcription counts as paused when any desktop channel reports a pause.
    return this.transcriptions.some(manager => manager.isTranscriptionPaused);
  }

  beginRecordingSession(args: {
    noteId: string;
    engine: RecordingEngineType;
    isRecordingWithoutTranscription: boolean;
    clearExisting: boolean;
  }) {
    this.session.beginRecordingSession(args);
    this.observability.beginSession();
  }

  restoreRecordingSessionFromState(state: PersistedAudioCaptureState) {
    this.session.restoreRecordingSessionFromState(state);
  }

  private completeCurrentSession() {
    this.session.completeCurrentSession();
  }

  updateSession(update: Partial<Omit<RecordingSessionUpdateArgs, "noteId" | "recordingId">>) {
    this.session.updateSession(update);
  }

  setAudioSilenceSince(value: number | null) {
    this.session.setAudioSilenceSince(value);
    this.observability.onSilenceChange(value);
  }

  updateUploadProgress(percent: number | null) {
    this.session.updateUploadProgress(percent);
  }

  /**
   * HELPER FUNCTIONS
   */

  _withAudioCaptureLock = (
    f: () => Promise<void>,
    options: { showErrorToast?: boolean } = { showErrorToast: true }
  ) => {
    return withAudioCaptureLock(this, f, options);
  };

  _updateDuration() {
    this.session.updateDuration();
  }

  handleMediaRecorderData = (ev: BlobEvent) => {
    if (ev.data.size === 0 || !this.activeNoteId) return;

    this.audioChunks.push(ev.data);
    void this.persistence.appendAudioBlob({
      noteId: this.activeNoteId,
      recordingId: this.currentSession?.recordingId,
      blob: ev.data,
    });
  };

  async _uploadRecording(
    payload: RecordingUploadPayload
  ): Promise<{ success: true } | { success: false; error: string }> {
    return this.processing.uploadRecording(payload);
  }

  async _transformAudioToNote(
    args: TransformAudioArgs
  ): Promise<{ success: boolean; error?: string }> {
    return this.processing.transformAudioToNote(args);
  }

  get isDesktop(): boolean {
    return clientEnvModule.isDesktop();
  }

  private getRecorderEngine(): RecorderEngine {
    return this.isDesktop ? this.desktopRecorder : this.webRecorder;
  }

  setRecallPermissionStatus(status: RecallPermissionMap) {
    this.device.setRecallPermissionStatus(status);
  }

  get screenCapturePermissionStatus(): RecallPermissionStatus | undefined {
    return this.recallPermissionStatus?.[RecallPermissionKind.ScreenCapture];
  }

  get microphonePermissionStatus(): RecallPermissionStatus | undefined {
    return this.recallPermissionStatus?.[RecallPermissionKind.Microphone];
  }

  get accessibilityPermissionStatus(): RecallPermissionStatus | undefined {
    return this.recallPermissionStatus?.[RecallPermissionKind.Accessibility];
  }

  async syncRecallPermissions(status: RecallPermissionMap) {
    await this.device.syncRecallPermissions(status);
  }

  setShowPermissionModal(show: boolean) {
    this.device.setShowPermissionModal(show);
  }

  setStatus(status: RecordingStatus) {
    this.session.setStatus({ status });
  }

  setPausedTime(value: number) {
    this.session.setPausedTime({ timestamp: value });
  }

  incrementTotalPausedTime(delta: number) {
    this.session.incrementTotalPausedTime({ delta });
  }

  clearDurationInterval() {
    this.session.clearDurationInterval();
  }

  startDurationInterval() {
    this.session.startDurationInterval();
  }

  resetAutoStopState() {
    this.session.resetAutoStopState();
  }

  setAutoStopModalVisible(open: boolean) {
    this.session.setAutoStopModalVisible({ open });
  }

  get autoStopDialogContent(): {
    title: string;
    message: string;
    limitLabel: string;
    currentDurationLabel: string | null;
  } | null {
    return this.session.getAutoStopDialogContent();
  }

  /**
   * STATE TRANSITIONS
   */
  // MONITORING
  async _detectAudioInputDevices(stream: MediaStream | null) {
    await this.device.detectAudioInputDevices(stream);
  }

  updateFriendlyDebugInfo(extra?: Record<string, unknown>) {
    this.ui.updateFriendlyDebugInfo(extra);
  }

  private _triggerAutoStop(reason: AutoStopReason) {
    triggerAutoStop(this, reason);
  }

  // Desktop path is orchestrated externally (DesktopRecorderOrchestrator)

  // IDLE (created) -> INITIALIZED (recording state restored) | IDLE (empty state)
  async initializeFromDb({ signal }: { signal: AbortSignal }) {
    await this.workflow.restoreFromDb({ signal });
  }

  startRecording({ noteId }: { noteId: string }) {
    if (this.device.getMissingDesktopPermission()) {
      this.setShowPermissionModal(true);
      return;
    }

    const engine = this.getRecorderEngine();
    this._withAudioCaptureLock(async () => {
      await engine.start(noteId);
    });
  }

  // RECORDING -> PAUSED
  async pauseRecording() {
    logger.info({
      message: "[AppStoreAudioCaptureStore] pauseRecording",
      info: { status: this.status },
    });
    return this.getRecorderEngine().pause();
  }

  // PAUSED -> RECORDING
  async resumeRecording() {
    logger.info({
      message: "[AppStoreAudioCaptureStore] resumeRecording",
      info: { status: this.status },
    });
    await this.getRecorderEngine().resume();
  }

  // RECORDING -> STOPPED
  async stopRecording() {
    logger.info({
      message: "[AppStoreAudioCaptureStore] stopRecording",
      info: { status: this.status },
    });
    try {
      await this.getRecorderEngine().stop();
    } finally {
      this.releaseRecordingLock?.(false);
      runInAction(() => {
        this.releaseRecordingLock = null;
        if (this.status === RecordingStatus.STOPPING) {
          this.status = RecordingStatus.STOPPED;
        }
      });
    }
  }

  // STOPPED -> UPLOADING -> UPLOADED -> PROCESSING -> PROCESSED
  async processRecording() {
    await this.workflow.processRecording();
  }

  // ANY -> IDLE
  resetRecording({ preserveLockedStatus }: { preserveLockedStatus?: boolean } = {}) {
    this.lifecycle.resetRecording({ preserveLockedStatus });
  }

  clearRecording() {
    this.lifecycle.clearRecording();
  }

  async clearAudioCapturesByNoteId(noteId: string) {
    await this.lifecycle.clearAudioCapturesByNoteId(noteId);
  }

  startDesktopInitializationBuffer(callback: () => void) {
    this.device.startDesktopInitializationBuffer(callback);
  }

  clearDesktopInitializationBuffer() {
    this.device.clearDesktopInitializationBuffer();
  }

  async hasRecordingForNote({ noteId }: { noteId: Uuid }): Promise<boolean> {
    return this.session.hasRecordingForNote({ noteId });
  }
}
